import { Component, Input, ElementRef, Host, Optional } from '@angular/core';

import * as _ from 'lodash';

/**
 * Torna blocos com tamanhos fixos e centralizados, podendo variar de acordo com o tamanho da tela do usuÃ¡rio.
 *
 * Ã uma abstraÃ§Ã£o para o [container do Bootstrap 4]{@link https://getbootstrap.com/docs/4.1/layout/overview/#containers}, com algumas melhorias e suporte a breakpoints.
 *
 * Demo: {@link https://gituex.gitlab.io/extensions/web-extensions#components/container}
 */
@Component({
  selector: 'uex-container',
  templateUrl: './container.component.html',
  host: {
    class: 'uex-container'
  }
})
export class ContainerComponent {
  /**
   * Tamanho fixo para o container
   *
   * Suporta: xs, sm, md, lg, xl, fluid
   */
  @Input('size') private set _setSize(size: ContainerSize) {
    this._size = size;
    this._updateClasses();
  }

  /**
   * Tamanho fixo para o container para telas de tamanho XS
   *
   * Suporta: xs, sm, md, lg, xl, fluid
   */
  @Input('size-xs') private set _setSizeXS(size: ContainerSize) {
    this._size_xs = size;
    this._updateClasses();
  }

  /**
   * Tamanho fixo para o container para telas de tamanho SM
   *
   * Suporta: xs, sm, md, lg, xl, fluid
   */
  @Input('size-sm') private set _setSizeSM(size: ContainerSize) {
    this._size_sm = size;
    this._updateClasses();
  }

  /**
   * Tamanho fixo para o container para telas de tamanho MD
   *
   * Suporta: xs, sm, md, lg, xl, fluid
   */
  @Input('size-md') private set _setSizeMD(size: ContainerSize) {
    this._size_md = size;
    this._updateClasses();
  }

  /**
   * Tamanho fixo para o container para telas de tamanho LG
   *
   * Suporta: xs, sm, md, lg, xl, fluid
   */
  @Input('size-lg') private set _setSizeLG(size: ContainerSize) {
    this._size_lg = size;
    this._updateClasses();
  }

  /**
   * Tamanho fixo para o container para telas de tamanho XL
   *
   * Suporta: xs, sm, md, lg, xl, fluid
   */
  @Input('size-xl') private set _setSizeXL(size: ContainerSize) {
    this._size_xl = size;
    this._updateClasses();
  }

  /**
   * @ignore
   */
  constructor(
    public elementRef: ElementRef
  ) { }

  private _size: ContainerSize;
  private _size_xs: ContainerSize;
  private _size_sm: ContainerSize;
  private _size_md: ContainerSize;
  private _size_lg: ContainerSize;
  private _size_xl: ContainerSize;

  private _classes: Object = {};
  private _old_classes: Array<string> = [];

  /**
   * Retorna a distÃ¢ncia do elemento para o topo do elemento pai mais prÃ³ximo posicionado com relative
   */
  public get offsetTop(): number {
    return this.elementRef.nativeElement.offsetTop;
  }

  private _isFluid(): boolean {
    return !this._size && !this._hasBreakPointSizes();
  }

  private _isXSmall(): boolean {
    return <any>this._size == 'xsmall' || this._size == 'xs';
  }

  private _isSmall(): boolean {
    return <any>this._size == 'small' || this._size == 'sm';
  }

  private _isMedium(): boolean {
    return <any>this._size == 'medium' || this._size == 'md';
  }

  private _isLarge(): boolean {
    return <any>this._size == 'large' || this._size == 'lg';
  }

  private _isXLarge(): boolean {
    return <any>this._size == 'xlarge' || this._size == 'xl';
  }

  private _hasBreakPointSizes(): boolean {
    return !!this._size_xs || !!this._size_sm || !!this._size_md || !!this._size_lg || !!this._size_xl;
  }

  /**
   * Valida quais serÃ£o as novas classes a serem aplicadas, e atualiza o elemento
   */
  private _updateClasses() {
    this._clearClasses();

    this._classes = {
      'uex-container': true,

      'container-fluid': this._isFluid(),
      'container-xs': this._isXSmall(),
      'container-sm': this._isSmall(),
      'container-md': this._isMedium(),
      'container-lg': this._isLarge(),
      'container-xl': this._isXLarge(),
    };

    if (this._hasBreakPointSizes()) {
      const sizes_info = [
        { value: this._size_xs, name: 'xs' },
        { value: this._size_sm, name: 'sm' },
        { value: this._size_md, name: 'md' },
        { value: this._size_lg, name: 'lg' },
        { value: this._size_xl, name: 'xl' }
      ];
      sizes_info.forEach((size_info, index: number) => {
        const has_prev_value = sizes_info.slice(0, index).length > 0;
        if (size_info.value) {
          this._classes[`container-${size_info.name}-${size_info.value}`] = true;
        } else if (!has_prev_value) {
          this._classes[`container-${size_info.name}-fluid`] = true;
        }
      });
    }

    this._setClasses();
  }

  /**
   * Limpa as classes aplicadas anteriormente no elemento
   */
  private _clearClasses() {
    if (this._old_classes) {
      const current_classes = this.elementRef.nativeElement.className.split(' ');
      this._old_classes.forEach((_class) => {
        const index = current_classes.indexOf(_class);
        if (index > -1) { current_classes.splice(index, 1); }
      });
      this.elementRef.nativeElement.className = current_classes.join(' ');
    }
  }

  /**
   * Aplica as classes atualizadas de acordo com as configuraÃ§Ãµes ao elemento
   */
  private _setClasses() {
    this._old_classes = [];
    _.forEach(this._classes, (_value, _key) => {
      if (_value) { this._old_classes.push(_key); }
    });
    const current_classes = this.elementRef.nativeElement.className.split(' ');
    this.elementRef.nativeElement.className = _.concat(current_classes, this._old_classes, ).join(' ');
  }
}

type ContainerSizeXS = 'xs' | 'xsmall';
type ContainerSizeSM = 'sm' | 'small';
type ContainerSizeMD = 'md' | 'medium';
type ContainerSizeLG = 'lg' | 'large';
type ContainerSizeXL = 'xl' | 'xlarge';
type ContainerSizeFluid = 'fluid';

/**
 * Tamanhos suportados pelos atributos
 */
export type ContainerSize = ContainerSizeXS | ContainerSizeSM | ContainerSizeMD | ContainerSizeLG | ContainerSizeXL | ContainerSizeFluid;
